import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Award, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function QuizDialog({ module, open, onOpenChange, onDone }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !module) return;
    setResult(null); setQuiz(null);
    api.get(`/modules/${module.id}/quiz`).then((r) => { setQuiz(r.data); setAnswers(Array(r.data.questions.length).fill(null)); });
  }, [open, module]);

  const submit = async () => {
    if (answers.some((a) => a === null)) { toast.error("Answer every question first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/modules/${module.id}/quiz`, { answers });
      setResult(data);
      toast[data.passed ? "success" : "warning"](data.passed ? `Passed with ${data.score}% — badge earned!` : `${data.score}% — ${data.pass_mark}% needed. Review and retry.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit quiz"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quiz-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Award className="w-5 h-5 text-[#C7761A]" /> Knowledge check — {module?.title}</DialogTitle>
          <DialogDescription>{quiz ? `${quiz.questions.length} questions · ${quiz.pass_mark}% to pass and earn the module badge.` : "Loading…"}</DialogDescription>
        </DialogHeader>

        {quiz && (
          <div className="space-y-5">
            {quiz.questions.map((qq, qi) => {
              const r = result?.results?.[qi];
              return (
                <div key={qq.q} data-testid={`quiz-q-${qi}`} className="p-4 rounded-xl border border-slate-200 bg-white">
                  <div className="text-sm font-semibold text-[#0A2A5E] flex gap-2"><span className="text-[#C7761A]">{qi + 1}.</span> {qq.q}</div>
                  <div className="mt-3 grid gap-2">
                    {qq.options.map((opt, oi) => {
                      const chosen = answers[qi] === oi;
                      let cls = chosen ? "border-[#0A2A5E] bg-[#F5F9FE]" : "border-slate-200 hover:border-[#F5A623] hover:bg-[#FFF7E8]";
                      if (r) {
                        if (oi === r.answer) cls = "border-emerald-500 bg-emerald-50";
                        else if (chosen) cls = "border-rose-400 bg-rose-50";
                        else cls = "border-slate-200 opacity-60";
                      }
                      return (
                        <button key={opt} type="button" disabled={!!result} data-testid={`quiz-q-${qi}-opt-${oi}`} onClick={() => setAnswers(answers.map((a, i) => (i === qi ? oi : a)))}
                          className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors flex items-center gap-2 ${cls}`}>
                          <span className={`w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-bold ${chosen ? "bg-[#0A2A5E] text-white border-[#0A2A5E]" : "border-slate-300 text-slate-500"}`}>{String.fromCharCode(65 + oi)}</span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {r && (
                    <div className={`mt-3 text-xs flex gap-2 items-start ${r.correct ? "text-emerald-700" : "text-rose-700"}`}>
                      {r.correct ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <XCircle className="w-4 h-4 flex-shrink-0" />}
                      <span>{r.explanation}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {result ? (
              <div data-testid="quiz-result" className={`p-4 rounded-xl text-center ${result.passed ? "bg-emerald-50 border border-emerald-200" : "bg-[#FFF7E8] border border-[#F5A623]"}`}>
                <div className="font-display text-3xl font-extrabold text-[#0A2A5E]">{result.score}%</div>
                <div className="text-sm mt-1">{result.passed ? "Passed — module badge earned." : `Not yet — ${result.pass_mark}% needed. Review the explanations and try again.`}</div>
                <div className="flex justify-center gap-2 mt-3">
                  {!result.passed && <Button data-testid="quiz-retry" variant="outline" onClick={() => { setResult(null); setAnswers(Array(quiz.questions.length).fill(null)); }}>Retry</Button>}
                  <Button data-testid="quiz-close" onClick={() => onOpenChange(false)} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">Close</Button>
                </div>
              </div>
            ) : (
              <Button data-testid="quiz-submit" onClick={submit} disabled={busy} className="w-full bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white h-11">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit answers"}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
